/**
 * Recruit ページ: KV イントロ、スクロール文字、SVG パス、リンクカードマスク
 *
 * @package Spice_Theme
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { initRevealLines } from '../animation/scroll-animations.js';

gsap.registerPlugin(ScrollTrigger, SplitText);

function initRecruitScrollChars() {
  document.querySelectorAll('.js-recruit-chars').forEach(el => {
    const split = new SplitText(el, {
      type: 'lines, chars',
      linesClass: 'line',
    });

    gsap.from(split.chars, {
      y: '100%',
      opacity: 0,
      stagger: 0.04,
      ease: 'power4.out',
      duration: 1,
      scrollTrigger: {
        trigger: el,
        start: 'top 80%',
        toggleActions: 'play none none none',
      },
    });
  });
}

function initRecruitFadeUp() {
  document.querySelectorAll('.js-recruit-fade-up').forEach(el => {
    gsap.from(el, {
      y: '8rem',
      opacity: 0,
      ease: 'power3.out',
      duration: 1,
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        toggleActions: 'play none none none',
      },
    });
  });
}

/**
 * Culture Deck ふきだし（静的 recruit.js の Decor Pop Animation に相当）
 */
function initRecruitAboutDecor() {
  const wrap = document.querySelector('.p-recruit-about__decor');
  if (!wrap) return;

  const decorBubble = wrap.querySelector('.p-recruit-about__decor-bubble');
  const decorText = wrap.querySelector('.p-recruit-about__decor-text');
  const decorEmoji = wrap.querySelector('.p-recruit-about__decor-emoji');
  const decorItems = [decorBubble, decorText, decorEmoji].filter(Boolean);
  if (decorItems.length === 0) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    gsap.set(decorItems, { scale: 1, opacity: 1 });
    return;
  }

  gsap.set(decorItems, { scale: 0, opacity: 0 });

  gsap.to(decorItems, {
    scale: 1,
    opacity: 1,
    ease: 'power3.out',
    stagger: 0.1,
    scrollTrigger: {
      trigger: wrap,
      start: 'top 60%',
      end: 'top 20%',
      scrub: 1,
    },
  });
}

function initRecruitAboutPath() {
  const wrap = document.querySelector('.p-recruit-about__svg-path');
  const path = document.querySelector('.p-recruit-about__svg-path svg path');
  if (!wrap || !path) return;

  const length = path.getTotalLength();

  gsap.set(path, {
    strokeDasharray: length,
    strokeDashoffset: length,
  });

  gsap.set(wrap, { autoAlpha: 1 });

  gsap.to(path, {
    strokeDashoffset: 0,
    ease: 'none',
    scrollTrigger: {
      trigger: '.p-recruit-about',
      start: 'top bottom',
      end: 'bottom bottom',
      scrub: true,
      invalidateOnRefresh: true,
    },
  });
}

/**
 * Recruit Links マスクホバー
 * 幅だけでは広いタブレット・タッチの互換マウスイベントで誤発火するため、
 * `(hover: hover) and (pointer: fine)` を必須にする（`p-recruit.scss` の hover も同条件）。
 */
const recruitLinksHoverMq = window.matchMedia(
  '(min-width: 1025px) and (hover: hover) and (pointer: fine)'
);

function recruitLinksHoverEnabled() {
  return recruitLinksHoverMq.matches;
}

function resetRecruitLinksMaskAnimations() {
  document.querySelectorAll('.p-recruit-links__item').forEach(item => {
    const maskPath = item.querySelector('.p-recruit-links__mask-path');
    const reveal = item.querySelector('.p-recruit-links__reveal');
    if (!maskPath) return;

    gsap.killTweensOf(maskPath);
    const len = maskPath.getTotalLength();
    gsap.set(maskPath, {
      strokeDasharray: len,
      strokeDashoffset: len,
      attr: { 'stroke-width': 100 },
    });
    if (reveal) gsap.set(reveal, { opacity: 0 });
  });
}

/**
 * 1件の `.p-recruit-links__item` について、リビール実寸に合わせて SVG mask の width / height と等比スケールを更新する。
 * maskUnits=userSpaceOnUse のまま固定数値だと縦横比がずれたときに歪むため、JS で寸法を同期する。
 *
 * @param {Element} item `.p-recruit-links__item`
 */
function syncRecruitLinkMaskItem(item) {
  const reveal = item.querySelector('.p-recruit-links__reveal');
  const maskSvg = item.querySelector('.p-recruit-links__mask');
  const maskEl = item.querySelector('mask');
  const maskBg = item.querySelector('.p-recruit-links__mask-bg');
  const scaleG = item.querySelector('.p-recruit-links__mask-scale');
  if (!reveal || !maskSvg || !maskEl || !maskBg || !scaleG) return;

  const dw = Number(maskSvg.dataset.designWidth) || 795;
  const dh = Number(maskSvg.dataset.designHeight) || 516;
  if (dw <= 0 || dh <= 0) return;

  const oxRaw = maskSvg.dataset.pathOffsetX;
  const oyRaw = maskSvg.dataset.pathOffsetY;
  const ox = oxRaw === undefined || oxRaw === '' ? 0 : Number(oxRaw);
  const oy = oyRaw === undefined || oyRaw === '' ? 0 : Number(oyRaw);

  const w = reveal.offsetWidth;
  const h = reveal.offsetHeight;
  if (w < 1 || h < 1) return;

  const s = Math.max(w / dw, h / dh);
  const tx = (w - dw * s) / 2;
  const ty = (h - dh * s) / 2;

  maskEl.setAttribute('width', String(w));
  maskEl.setAttribute('height', String(h));
  maskBg.setAttribute('width', String(w));
  maskBg.setAttribute('height', String(h));
  // SVG は右端の transform から点に適用されるため、設計座標上の path オフセットは scale の後ろ（右）に置く
  scaleG.setAttribute(
    'transform',
    `translate(${tx} ${ty}) scale(${s}) translate(${Number.isFinite(ox) ? ox : 0} ${Number.isFinite(oy) ? oy : 0})`
  );
}

/**
 * 募集リンク各アイテムのマスクレイアウトを初期化し、リサイズ時に `syncRecruitLinkMaskItem` で追従する。
 */
function initRecruitLinksMaskLayout() {
  const section = document.querySelector('.p-recruit-links');
  if (!section) return;

  section.querySelectorAll('.p-recruit-links__item').forEach(item => {
    syncRecruitLinkMaskItem(item);
    const reveal = item.querySelector('.p-recruit-links__reveal');
    if (!reveal) return;
    const ro = new ResizeObserver(() => {
      syncRecruitLinkMaskItem(item);
    });
    ro.observe(reveal);
  });
}

function initRecruitLinkMasks() {
  document.querySelectorAll('.p-recruit-links__item').forEach(item => {
    const maskPath = item.querySelector('.p-recruit-links__mask-path');
    const revealImg = item.querySelector('.p-recruit-links__reveal-img');
    if (!maskPath || !revealImg) return;

    const length = maskPath.getTotalLength();

    gsap.set(maskPath, {
      strokeDasharray: length,
      strokeDashoffset: length,
      attr: { 'stroke-width': 100 },
    });

    let tl;

    item.addEventListener('mouseenter', () => {
      if (!recruitLinksHoverEnabled()) return;
      if (tl) tl.kill();

      const reveal = item.querySelector('.p-recruit-links__reveal');
      if (reveal) gsap.set(reveal, { opacity: 1 });

      tl = gsap.timeline();
      tl.to(
        maskPath,
        {
          attr: { 'stroke-width': 220 },
          duration: 0.8,
          ease: 'power2.out',
        },
        0
      ).to(
        maskPath,
        {
          strokeDashoffset: 0,
          duration: 1.5,
          ease: 'power2.out',
        },
        0
      );
    });

    item.addEventListener('mouseleave', () => {
      if (!recruitLinksHoverEnabled()) return;
      if (tl) tl.kill();

      const reveal = item.querySelector('.p-recruit-links__reveal');
      tl = gsap.timeline();
      tl.to(maskPath, {
        strokeDashoffset: length,
        attr: { 'stroke-width': 100 },
        duration: 0.6,
        ease: 'power2.inOut',
      });
      if (reveal) tl.set(reveal, { opacity: 0 });
    });
  });

  if (!recruitLinksHoverEnabled()) {
    resetRecruitLinksMaskAnimations();
  }

  const onHoverMqChange = () => {
    if (!recruitLinksHoverEnabled()) {
      resetRecruitLinksMaskAnimations();
    }
  };
  if (typeof recruitLinksHoverMq.addEventListener === 'function') {
    recruitLinksHoverMq.addEventListener('change', onHoverMqChange);
  } else {
    recruitLinksHoverMq.addListener(onHoverMqChange);
  }
}

window.addEventListener('load', () => {
  document.fonts.ready.then(() => {
    initRevealLines();

    const runIdle = () => {
      initRecruitAboutPath();
      ScrollTrigger.refresh();
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(runIdle, { timeout: 1500 });
    } else {
      setTimeout(runIdle, 300);
    }
  });
});

/** @type {(() => void) | null} */
let recruitGalleryTeardown = null;

function buildRecruitGalleryImageUrls(base, count) {
  const urls = [];
  const n = Math.max(0, Math.min(99, count));
  for (let i = 1; i <= n; i += 1) {
    urls.push(`${base}${String(i).padStart(2, '0')}.jpg`);
  }
  return urls;
}

/**
 * グリッド seed 用 stride。rows をエンコードする幅（cols との衝突を防ぐ）。
 * 32rem タイル想定で rows は通常 50 未満。極端な縦長画面でも 512 を超えない想定。
 */
const RECRUIT_GALLERY_GRID_SEED_ROWS_STRIDE = 512;

/**
 * cols / rows から決定論的シャッフル用 seed を生成する。
 *
 * @param {number} cols
 * @param {number} rows
 * @returns {number}
 */
function encodeRecruitGalleryGridSeed(cols, rows) {
  return cols * RECRUIT_GALLERY_GRID_SEED_ROWS_STRIDE + rows;
}

/**
 * 決定論的シャッフル（同一グリッドサイズではリサイズ後も同じ並び）
 *
 * @param {number} count
 * @param {number} seed
 * @returns {number[]}
 */
function createRecruitGalleryShuffleOrder(count, seed) {
  const order = Array.from({ length: count }, (_, i) => i);
  let state = seed >>> 0;

  for (let i = count - 1; i > 0; i -= 1) {
    state = (state * 1664525 + 1013904223) >>> 0;
    const j = state % (i + 1);
    [order[i], order[j]] = [order[j], order[i]];
  }

  return order;
}

/**
 * チェッカーボード配置で上下左右の隣接タイルに同じ画像を割り当てない
 * （cols は偶数にすることでラップ時の左右隣接も担保）
 *
 * @param {number} r
 * @param {number} c
 * @param {number} cols
 * @param {number} imageCount
 * @returns {number}
 */
function getRecruitGalleryTileImageIndex(r, c, cols, imageCount) {
  if (imageCount <= 1) {
    return 0;
  }

  const parity = (r + c) % 2;
  const slot = Math.floor((r * cols + c) / 2);
  const halfCount = Math.ceil(imageCount / 2);
  const offset = parity * halfCount;

  return (offset + (slot % halfCount)) % imageCount;
}

function initRecruitGalleryStatic(grid, images) {
  grid.innerHTML = '';
  images.forEach(src => {
    const tile = document.createElement('div');
    tile.className = 'p-recruit-gallery__tile';
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    tile.appendChild(img);
    grid.appendChild(tile);
  });
}

function initRecruitGalleryDrag(section, grid, cursor, images) {
  grid.innerHTML = '';

  const tempTile = document.createElement('div');
  tempTile.className = 'p-recruit-gallery__tile';
  grid.appendChild(tempTile);
  const BASE_W = tempTile.offsetWidth;
  const BASE_H = tempTile.offsetHeight;
  grid.removeChild(tempTile);
  const GAP = 0;
  const CELL_W = BASE_W + GAP;
  const CELL_H = BASE_H + GAP;

  let COLS = Math.ceil(window.innerWidth / CELL_W) + 2;
  let ROWS = Math.ceil(window.innerHeight / CELL_H) + 2;
  // トーラス状ラップ時も左右・上下の隣接で同画像にならないよう偶数グリッドにする
  if (COLS % 2 === 1) {
    COLS += 1;
  }
  if (ROWS % 2 === 1) {
    ROWS += 1;
  }

  const TOTAL_W = COLS * CELL_W;
  const TOTAL_H = ROWS * CELL_H;
  const shuffleOrder = createRecruitGalleryShuffleOrder(
    images.length,
    encodeRecruitGalleryGridSeed(COLS, ROWS)
  );

  /** @type {{ el: HTMLElement; x: number; y: number }[]} */
  const tiles = [];

  for (let r = 0; r < ROWS; r += 1) {
    for (let c = 0; c < COLS; c += 1) {
      const tile = document.createElement('div');
      tile.className = 'p-recruit-gallery__tile';
      const img = document.createElement('img');
      const imageIndex = getRecruitGalleryTileImageIndex(
        r,
        c,
        COLS,
        images.length
      );
      img.src = images[shuffleOrder[imageIndex]];
      img.alt = '';
      img.decoding = 'async';
      tile.appendChild(img);
      grid.appendChild(tile);

      const x = c * CELL_W - (TOTAL_W - window.innerWidth) / 2;
      const y = r * CELL_H - (TOTAL_H - window.innerHeight) / 2;

      gsap.set(tile, { x, y });
      tiles.push({ el: tile, x, y });

      tile.addEventListener('mouseenter', () => {
        gsap.to(img, { scale: 1.3, duration: 0.4, ease: 'power2.out' });
      });
      tile.addEventListener('mouseleave', () => {
        gsap.to(img, { scale: 1, duration: 0.4, ease: 'power2.out' });
      });
    }
  }

  function moveTiles(dx, dy) {
    tiles.forEach(tile => {
      tile.x += dx;
      tile.y += dy;

      if (tile.x + BASE_W < 0) tile.x += TOTAL_W;
      if (tile.x > window.innerWidth) tile.x -= TOTAL_W;
      if (tile.y + BASE_H < 0) tile.y += TOTAL_H;
      if (tile.y > window.innerHeight) tile.y -= TOTAL_H;

      gsap.set(tile.el, { x: tile.x, y: tile.y });
    });
  }

  let isDragging = false;
  let lastX = 0;
  let lastY = 0;
  let velX = 0;
  let velY = 0;
  /** @type {import('gsap').Tween | null} */
  let momentumAnim = null;

  const ac = new AbortController();
  const signal = ac.signal;

  const onPointerDown = e => {
    if (e.button !== 0) return;
    isDragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
    velX = 0;
    velY = 0;
    if (momentumAnim) momentumAnim.kill();
    section.setPointerCapture(e.pointerId);
  };

  const onPointerMove = e => {
    if (!isDragging) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    velX = velX * 0.7 + dx * 0.3;
    velY = velY * 0.7 + dy * 0.3;
    lastX = e.clientX;
    lastY = e.clientY;
    moveTiles(dx, dy);
  };

  const endDrag = e => {
    if (!isDragging) return;
    isDragging = false;
    try {
      section.releasePointerCapture(e.pointerId);
    } catch {
      // キャプチャされていない場合は無視
    }
    const momentum = { vx: velX, vy: velY };
    momentumAnim = gsap.to(momentum, {
      vx: 0,
      vy: 0,
      duration: 3.5,
      ease: 'power3.out',
      onUpdate: () => moveTiles(momentum.vx, momentum.vy),
    });
  };

  section.addEventListener('pointerdown', onPointerDown, { signal });
  section.addEventListener('pointermove', onPointerMove, { signal });
  section.addEventListener('pointerup', endDrag, { signal });
  section.addEventListener('pointercancel', endDrag, { signal });

  if (cursor) {
    cursor.classList.add('is-visible');
    section.addEventListener(
      'pointerdown',
      () => {
        cursor.classList.remove('is-visible');
      },
      { once: true, signal }
    );
  }

  recruitGalleryTeardown = () => {
    ac.abort();
    if (momentumAnim) momentumAnim.kill();
    grid.innerHTML = '';
  };
}

function initRecruitGallery() {
  const section = document.querySelector('.p-recruit-gallery');
  const grid = document.getElementById('js-recruit-gallery-grid');
  if (!section || !grid) return;

  if (recruitGalleryTeardown) {
    recruitGalleryTeardown();
    recruitGalleryTeardown = null;
  }

  section.classList.remove('p-recruit-gallery--static');

  const base = section.dataset.recruitGalleryBase || '';
  const count = parseInt(section.dataset.recruitGalleryCount || '32', 10);
  const images = buildRecruitGalleryImageUrls(
    base,
    Number.isFinite(count) ? count : 32
  );
  if (images.length === 0) return;

  const cursor = document.getElementById('js-recruit-gallery-cursor');
  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  if (reduceMotion) {
    section.classList.add('p-recruit-gallery--static');
    initRecruitGalleryStatic(grid, images);
    if (cursor) cursor.classList.remove('is-visible');
    return;
  }

  initRecruitGalleryDrag(section, grid, cursor, images);
}

let recruitGalleryResizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(recruitGalleryResizeTimer);
  recruitGalleryResizeTimer = window.setTimeout(() => {
    initRecruitGallery();
  }, 200);
});

function initRecruitFaqAccordion() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll('.p-recruit-faq .c-faq__item').forEach(item => {
    const trigger = item.querySelector('.c-faq__trigger');
    const answer = item.querySelector('.c-faq__answer');
    if (!trigger || !answer) return;

    // JS 初期化前に native toggle が走っていた場合の同期
    if (item.hasAttribute('open')) {
      gsap.set(answer, { height: 'auto', overflow: '' });
    }

    trigger.addEventListener('click', e => {
      e.preventDefault();
      gsap.killTweensOf(answer);

      if (!item.hasAttribute('open')) {
        item.setAttribute('open', '');
        gsap.set(answer, { height: 0, overflow: 'hidden' });
        gsap.to(answer, {
          height: 'auto',
          duration: 0.4,
          ease: 'power3.out',
          onComplete: () => gsap.set(answer, { height: '', overflow: '' }),
        });
      } else {
        gsap.set(answer, { height: answer.offsetHeight, overflow: 'hidden' });
        gsap.to(answer, {
          height: 0,
          duration: 0.3,
          ease: 'power3.in',
          onComplete: () => {
            item.removeAttribute('open');
            gsap.set(answer, { height: '', overflow: '' });
          },
        });
      }
    });
  });
}

function initRecruitBanner() {
  const banner = document.querySelector('.js-recruit-banner');
  const closeBtn = banner?.querySelector('.js-recruit-banner-close');
  if (!banner || !closeBtn) return;

  // リロード・ページ遷移のたびに再表示する仕様（意図的）
  setTimeout(() => {
    banner.classList.add('is-visible');
  }, 1000);

  closeBtn.addEventListener('click', () => {
    banner.classList.remove('is-visible');
    banner.classList.add('is-hidden');
    document.body.focus();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initRecruitScrollChars();
  initRecruitFadeUp();
  initRecruitAboutDecor();
  initRecruitLinksMaskLayout();
  initRecruitLinkMasks();
  initRecruitGallery();
  initRecruitFaqAccordion();
  initRecruitBanner();
});
